import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { Card, Badge, Accordion, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { patchEstabilidadBulk } from '../../../../api/common';
import AutoguardadoIndicator from '../components/AutoguardadoIndicator';
import EstabilidadTable from '../components/EstabilidadTable';
import { convertirEstabilidadAEsquema, convertirEsquemaAEstabilidad } from '../../../../helpers/conversionEsquemas';

const StepEstabilidad = forwardRef(({ loteEquipos, lotesCalibracion, plantilla, onDataChange }, ref) => {
     
  const [datosEstabilidad, setDatosEstabilidad] = useState({});

  const [datosPuntosCalibracion, setDatosPuntosCalibracion] = useState({});
  const [autoguardado, setAutoguardado] = useState('idle');
  const timerRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════
  // INICIALIZAR PUNTOS DE CALIBRACIÓN (compartidos desde StepIBC)
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0].sesiones) {
      return;
    }

    const puntosCal = {};

    (lotesCalibracion[0].sesiones || []).forEach((sesion) => {
      puntosCal[sesion.magnitud] = sesion.puntosCalibracion || [];
    });

    setDatosPuntosCalibracion(puntosCal);
  }, [lotesCalibracion]);

  // ═══════════════════════════════════════════════════════════════
  // INICIALIZAR DATOS DE ESTABILIDAD
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0].sesiones) {
      return;
    }
    const datos = {};
    (lotesCalibracion[0].sesiones || []).forEach((sesion) => {
      const estabilidadDB = sesion?.estabilidad;

      if (estabilidadDB && estabilidadDB.length > 0) {
        // Convertir del esquema DB al formato UI
        datos[sesion.magnitud] = convertirEsquemaAEstabilidad(estabilidadDB, sesion);
      } else {
        // Crear estructura vacía con arrays dinámicos
        datos[sesion.magnitud] = {
          patronUsado: '',
          criterio: '', // compat (deprecado — usar `criterios[]`)
          criterios: Array(sesion.cantidadPuntos).fill(''),
          puntos: Array(sesion.cantidadPuntos).fill(null).map(() => [])
        };
      }
    });

    setDatosEstabilidad(datos);
  }, [lotesCalibracion]);

  const guardarCambios = useCallback(async (datos) => {
    if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0].sesiones) {
      return;
    }
    setAutoguardado('saving');
    try {
      // Convertir datos de cada magnitud al esquema de DB
      const estabilidadPorSesion = [];
      
      lotesCalibracion[0].sesiones.forEach((sesionConfig, sesionIndex) => {
        const magnitudNombre = sesionConfig.magnitud;
        const datosMagnitud = datos[magnitudNombre];
        
        if (!datosMagnitud) return;
        
        const puntosCalibracion = datosPuntosCalibracion[magnitudNombre] || [];
        const config = {
          patronUsado: datosMagnitud.patronUsado,
          criterio: datosMagnitud.criterio,       // compat
          criterios: datosMagnitud.criterios || [] // per-punto
        };
        
        // Convertir al esquema de DB
        const estabilidadArray = convertirEstabilidadAEsquema(
          datosMagnitud.puntos || [],
          puntosCalibracion,
          config,
          sesionConfig
        );
        
        estabilidadPorSesion.push(estabilidadArray || []);
      });
      
      
      await patchEstabilidadBulk(loteEquipos._id, { estabilidad: estabilidadPorSesion });
      setAutoguardado('saved');
      if (onDataChange) onDataChange();
      setTimeout(() => setAutoguardado('idle'), 2000);
    } catch (error) {
      console.error('Error guardando estabilidad:', error);
      setAutoguardado('error');
    }
  }, [loteEquipos, lotesCalibracion, datosPuntosCalibracion, onDataChange]);

  // Cambio en mediciones de estabilidad por punto
  const handleChangeMedicion = (magnitud, puntoIdx, medIdx, valor) => {
    const nuevosDatos = JSON.parse(JSON.stringify(datosEstabilidad));
    
    // Asegurar que el array del punto exista
    if (!nuevosDatos[magnitud].puntos[puntoIdx]) {
      nuevosDatos[magnitud].puntos[puntoIdx] = [];
    }
    
    // Asignar el valor en el índice específico
    // Si el valor es vacío y es el último elemento, eliminarlo para mantener el array dinámico
    if (valor === '') {
      // Si es el último elemento y está vacío, reducir el array
      if (medIdx === nuevosDatos[magnitud].puntos[puntoIdx].length - 1) {
        nuevosDatos[magnitud].puntos[puntoIdx].pop();
      } else {
        // Si no es el último, mantener el espacio pero con valor vacío
        nuevosDatos[magnitud].puntos[puntoIdx][medIdx] = '';
      }
    } else {
      // Si el índice está fuera del tamaño actual, expandir el array
      while (nuevosDatos[magnitud].puntos[puntoIdx].length <= medIdx) {
        nuevosDatos[magnitud].puntos[puntoIdx].push('');
      }
      nuevosDatos[magnitud].puntos[puntoIdx][medIdx] = parseFloat(valor);
    }
    
    setDatosEstabilidad(nuevosDatos);

    // Autoguardado con debounce
    if (timerRef.current) clearTimeout(timerRef.current);
    setAutoguardado('pending');
    timerRef.current = setTimeout(() => {
      guardarCambios(nuevosDatos);
    }, 6000);
  };

  // Cambio en criterio por punto — cada punto tiene su propio criterio de
  // variación máxima permitida.
  const handleChangeCriterioPunto = (magnitud, puntoIdx, valor) => {
    const nuevosDatos = JSON.parse(JSON.stringify(datosEstabilidad));
    if (!Array.isArray(nuevosDatos[magnitud].criterios)) {
      nuevosDatos[magnitud].criterios = [];
    }
    // Expandir array si es necesario.
    while (nuevosDatos[magnitud].criterios.length <= puntoIdx) {
      nuevosDatos[magnitud].criterios.push('');
    }
    nuevosDatos[magnitud].criterios[puntoIdx] = valor === '' ? '' : parseFloat(valor);
    setDatosEstabilidad(nuevosDatos);

    if (timerRef.current) clearTimeout(timerRef.current);
    setAutoguardado('pending');
    timerRef.current = setTimeout(() => {
      guardarCambios(nuevosDatos);
    }, 2000);
  };

  // Cleanup del timer
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const guardarAhora = useCallback(() => {
    // Cancelar timer pendiente
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Guardar inmediatamente si hay cambios pendientes
    if (autoguardado === 'pending') {
      return guardarCambios(datosEstabilidad);
    }
    // Si no hay cambios pendientes, retornar promesa resuelta
    return Promise.resolve();
  }, [datosEstabilidad, autoguardado, guardarCambios]);

  // Exponer el método guardarAhora al componente padre
  useImperativeHandle(ref, () => ({
    guardarAhora,
    tieneCambiosPendientes: () => autoguardado === 'pending'
  }), [guardarAhora, autoguardado]);

  // Calcular cumplimiento por punto — usa el criterio específico del punto.
  const calcularCumplimientoPunto = (mediciones, criterio) => {
    const validos = mediciones.filter(m => m !== '' && !isNaN(m));
    if (validos.length < 2 || !criterio || Number.isNaN(Number(criterio))) return null;

    const max = Math.max(...validos);
    const min = Math.min(...validos);
    return (max - min) <= Number(criterio);
  };

  // Calcular cumplimiento general de la magnitud — todos los puntos con datos
  // deben cumplir su respectivo criterio.
  const calcularCumplimientoMagnitud = (datos) => {
    if (!datos.puntos) return null;
    const criterios = datos.criterios || [];

    const resultados = datos.puntos.map((mediciones, i) => {
      const criterioPunto = criterios[i] ?? datos.criterio;
      return calcularCumplimientoPunto(mediciones, criterioPunto);
    });

    const puntosConDatos = resultados.filter(r => r !== null);
    if (puntosConDatos.length === 0) return null;
    return puntosConDatos.every(r => r === true);
  };

  // Verificar que existan sesiones antes de renderizar
  if (!lotesCalibracion || lotesCalibracion.length === 0 || !lotesCalibracion[0].sesiones) {
    return (
      <Alert variant="warning">
        <Alert.Heading>No hay sesiones disponibles</Alert.Heading>
        <p>Debe completar los pasos anteriores antes de continuar con estabilidad.</p>
      </Alert>
    );
  }

  // Todas las sesiones se listan siempre — para las magnitudes que NO requieren
  // estabilidad en el lote, se muestra un mensaje explícito dentro del
  // accordion en vez de omitirlas. Esto le da al usuario visibilidad del scope
  // completo (temperatura + humedad + …) y evita que asuma que faltó cargar
  // datos donde en realidad no aplicaba.
  //
  // Prioridad para resolver si una magnitud requiere estabilidad:
  //   1. `sesion.requiereEstabilidad` (persistido en el lote, respeta la
  //      personalización hecha en Step 1).
  //   2. Fallback a `plantilla.magnitudesConfig[i].requiereEstabilidad` para
  //      lotes creados antes del nuevo campo persistido.
  const magnitudesConfig = plantilla?.magnitudesConfig || [];
  const todasLasSesiones = lotesCalibracion[0].sesiones || [];
  const requiereEstabilidadDe = (sesion) => {
    if (typeof sesion?.requiereEstabilidad === 'boolean') return sesion.requiereEstabilidad;
    const config = magnitudesConfig.find((m) => m.nombre === sesion.magnitud);
    return config ? config.requiereEstabilidad !== false : true;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Prueba de Estabilidad</h5>
        <AutoguardadoIndicator 
          estado={autoguardado} 
          onReintentar={() => guardarCambios(datosEstabilidad)}
          onGuardarAhora={guardarAhora}
        />
      </div>

      <Alert variant="info">
        <strong>Instrucción:</strong> Registre las mediciones de estabilidad para cada punto de calibración.
        El sistema validará automáticamente según el criterio definido.
      </Alert>

      <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center w-100" style={{borderRadius: '12px 12px 0 0'}}>
          <span>Estabilidad por Magnitud</span>
        </Card.Header>
        <Card.Body className="p-2 w-100">
          <Accordion defaultActiveKey="0">
            {todasLasSesiones.map((sesion, idxMag) => {
              const datos = datosEstabilidad[sesion.magnitud] || {};
              const cumpleMagnitud = calcularCumplimientoMagnitud(datos);
              const aplica = requiereEstabilidadDe(sesion);

              return (
                <Accordion.Item key={idxMag} eventKey={idxMag.toString()}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center gap-2 w-100">
                      <span>{sesion.magnitud}</span>
                      {!aplica ? (
                        <Badge bg="secondary" className="ms-2">Sin estabilidad</Badge>
                      ) : (
                        cumpleMagnitud !== null && (
                          <Badge bg={cumpleMagnitud ? 'success' : 'danger'} className="ms-2">
                            {cumpleMagnitud ? <><FaCheckCircle className="me-1" /> CUMPLE</> : <><FaTimesCircle className="me-1" /> NO CUMPLE</>}
                          </Badge>
                        )
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    {!aplica ? (
                      <Alert variant="secondary" className="mb-0">
                        Para la magnitud <strong>{sesion.magnitud}</strong> la plantilla
                        no tiene configurada la prueba de estabilidad. No hay nada por
                        registrar aquí — puedes continuar al siguiente paso.
                      </Alert>
                    ) : (
                      <div>
                        <h6 className="mb-3 text-dark">
                          📊 Mediciones y Criterio por Punto
                          <small className="ms-2 text-muted">
                            ({sesion.cantidadPuntos} {sesion.cantidadPuntos === 1 ? 'punto' : 'puntos'})
                          </small>
                        </h6>
                        <Alert variant="info" className="py-2 small mb-3">
                          Registrá el <strong>criterio</strong> (variación máxima permitida)
                          de cada punto. El sistema valida punto por punto.
                          Los patrones ya fueron seleccionados en el paso 1.
                        </Alert>
                        <EstabilidadTable
                          magnitud={sesion}
                          datosEstabilidad={datos.puntos || []}
                          datosPuntosCalibracion={datosPuntosCalibracion[sesion.magnitud] || []}
                          criteriosPorPunto={datos.criterios || []}
                          onChangeMedicion={(puntoIdx, medIdx, valor) =>
                            handleChangeMedicion(sesion.magnitud, puntoIdx, medIdx, valor)
                          }
                          onChangeCriterio={(puntoIdx, valor) =>
                            handleChangeCriterioPunto(sesion.magnitud, puntoIdx, valor)
                          }
                        />

                        {/* Indicadores de cumplimiento por punto */}
                        {datos.puntos && (
                          <div className="mt-3">
                            <small className="text-muted">Estado por punto:</small>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {datos.puntos.map((mediciones, pIdx) => {
                                const criterioPunto = (datos.criterios || [])[pIdx] ?? datos.criterio;
                                const cumple = calcularCumplimientoPunto(mediciones, criterioPunto);
                                const valorPunto = datosPuntosCalibracion[sesion.magnitud]?.[pIdx] || `Punto ${pIdx + 1}`;

                                return cumple !== null ? (
                                  <Badge key={pIdx} bg={cumple ? 'success' : 'danger'} className="d-flex align-items-center gap-1">
                                    {cumple ? <FaCheckCircle /> : <FaTimesCircle />}
                                    {valorPunto}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Card.Body>
      </Card>
    </div>
  );
});

export default StepEstabilidad;
